import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

import type { AdminUser as UserRow } from "../api/types";
import { useApiList, useMutation } from "../api/hooks";
import { CrudForm, type FieldSpec } from "../components/CrudPage";
import { Badge, Bool, DataTable, fmtDate, Page, Pagination, SearchBox, Toolbar, useToast } from "../components/ui";

const ROLES = [
  { value: "client", label: "Customer" },
  { value: "driver", label: "Driver" },
  { value: "manager", label: "Vendor manager" },
  { value: "city_admin", label: "City admin" },
  { value: "admin", label: "Admin" },
];

function fields(vendors: { value: string; label: string }[]): FieldSpec[] {
  return [
    { name: "name", label: "Name", required: true },
    { name: "email", label: "Email", required: true, createOnly: true },
    { name: "phone", label: "Phone", placeholder: "+91…" },
    { name: "role", label: "Role", type: "select", options: ROLES, required: true },
    { name: "vendor_id", label: "Vendor (managers)", type: "select", options: vendors },
    { name: "commission_pct", label: "Driver commission %", type: "number", decimal: true, hint: "Share of the delivery fee the driver keeps (02 §7)" },
    { name: "password", label: "Password", type: "password", required: true, createOnly: true, hint: "At least 8 characters" },
    { name: "password", label: "New password", type: "password", editOnly: true, hint: "Leave empty to keep; changing it signs the user out everywhere" },
    { name: "is_active", label: "Active", type: "switch", editOnly: true, hint: "Deactivating revokes every session" },
  ];
}

export function UsersPage() {
  const [params, setParams] = useSearchParams();
  const role = params.get("role") ?? "";
  const [q, setQ] = useState(params.get("q") ?? "");
  const [active, setActive] = useState("");
  const [page, setPage] = useState(1);
  const query = useMemo(() => ({ q: q || undefined, role: role || undefined, is_active: active === "" ? undefined : active === "1", page, per_page: 25 }), [q, role, active, page]);
  const list = useApiList<UserRow>("/admin/users", query);
  const vendors = useApiList<{ id: string; name: string }>("/admin/vendors", { per_page: 100 });
  const vendorOptions = vendors.data.map((v) => ({ value: v.id, label: v.name }));
  const [editing, setEditing] = useState<UserRow | null | "new">(null);
  const [preset, setPreset] = useState<Record<string, unknown>>({});
  const { run, busy } = useMutation();
  const toast = useToast();

  // Deep link from the vendor page: /users?new=manager&vendor_id=…
  useEffect(() => {
    if (params.get("new") === "manager") {
      setPreset({ role: "manager", vendor_id: params.get("vendor_id") ?? "" });
      setEditing("new");
      setParams({});
    }
  }, [params, setParams]);

  return (
    <Page
      title="Users"
      actions={
        <button type="button" className="btn btn-primary" onClick={() => (setPreset({}), setEditing("new"))}>
          + New user
        </button>
      }
    >
      <Toolbar>
        <SearchBox value={q} onChange={(v) => (setQ(v), setPage(1))} placeholder="Name, email or phone…" />
        <select className="input" value={role} onChange={(e) => (setParams(e.target.value ? { role: e.target.value } : {}), setPage(1))}>
          <option value="">Role: all</option>
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <select className="input" value={active} onChange={(e) => (setActive(e.target.value), setPage(1))}>
          <option value="">Active: all</option>
          <option value="1">Active</option>
          <option value="0">Deactivated</option>
        </select>
      </Toolbar>
      <DataTable
        columns={[
          { key: "name", label: "Name" },
          { key: "email", label: "Email" },
          { key: "phone", label: "Phone", render: (u) => u.phone ?? "—" },
          { key: "role", label: "Role", render: (u) => <Badge tone="info">{ROLES.find((r) => r.value === u.role)?.label ?? u.role}</Badge> },
          { key: "vendor_name", label: "Vendor", render: (u) => u.vendor_name ?? "—" },
          { key: "is_active", label: "Active", render: (u) => <Bool value={u.is_active} /> },
          {
            key: "extra",
            label: "",
            render: (u) =>
              u.role === "driver" ? (
                <span className="muted">
                  {u.is_online ? "online" : "offline"} · {u.commission_pct}%{u.rating_avg ? ` · ★ ${Number(u.rating_avg).toFixed(1)}` : ""}
                </span>
              ) : (
                ""
              ),
          },
          { key: "created_at", label: "Joined", render: (u) => fmtDate(u.created_at) },
        ]}
        rows={list.data}
        loading={list.loading}
        error={list.error}
        onRowClick={(u) => setEditing(u)}
      />
      <Pagination page={page} perPage={25} total={list.total} onPage={setPage} />
      {editing !== null && (
        <CrudForm
          title={editing === "new" ? "New user" : `Edit ${editing.name}`}
          fields={fields(vendorOptions)}
          initial={editing === "new" ? preset : (editing as unknown as Record<string, unknown>)}
          isNew={editing === "new"}
          busy={busy}
          onClose={() => setEditing(null)}
          onSubmit={async (values) => {
            const r =
              editing === "new"
                ? await run("/admin/users", { method: "POST", body: values })
                : await run(`/admin/users/${editing.id}`, { method: "PATCH", body: values });
            if (r.ok) {
              toast("Saved");
              setEditing(null);
              list.reload();
            } else toast(r.error, "err");
          }}
        />
      )}
    </Page>
  );
}
